import { env } from "@/env";

export class ApiError extends Error {
  readonly status: number;
  readonly body: unknown;

  constructor(message: string, status: number, body: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.body = body;
  }
}

type ApiJsonOptions = Omit<RequestInit, "body"> & {
  apiBaseUrl?: string;
  json?: unknown;
  body?: BodyInit | null;
};

type ApiFormOptions = Omit<RequestInit, "body"> & {
  apiBaseUrl?: string;
};

export async function apiJson<T>(
  path: string,
  options: ApiJsonOptions = {},
): Promise<T> {
  const { apiBaseUrl = env.API_BASE_URL, json, ...init } = options;
  const headers = new Headers(init.headers);
  const body = json === undefined ? init.body : JSON.stringify(json);

  if (json !== undefined && !headers.has("content-type")) {
    headers.set("content-type", "application/json");
  }

  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    credentials: "include",
    headers,
    body,
  });

  return readApiResponse<T>(response);
}

export async function apiForm<T>(
  path: string,
  form: FormData,
  options: ApiFormOptions = {},
): Promise<T> {
  const { apiBaseUrl = env.API_BASE_URL, ...init } = options;
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...init,
    method: init.method ?? "POST",
    credentials: "include",
    body: form,
  });

  return readApiResponse<T>(response);
}

/**
 * Multipart POST via XMLHttpRequest so callers can observe when the request
 * body has finished uploading (fetch cannot signal this) — used to split the
 * enrollment "Uploading" phase from the server-side "Checking sample" phase.
 */
export function apiFormUpload<T>(
  path: string,
  form: FormData,
  options: ApiFormOptions & { onUploadComplete?: () => void } = {},
): Promise<T> {
  const { apiBaseUrl = env.API_BASE_URL, onUploadComplete } = options;
  return new Promise<T>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `${apiBaseUrl}${path}`);
    xhr.withCredentials = true;
    if (onUploadComplete) {
      xhr.upload.addEventListener("loadend", () => onUploadComplete());
    }
    xhr.addEventListener("error", () =>
      reject(new ApiError("network error", 0, undefined)),
    );
    xhr.addEventListener("abort", () =>
      reject(new ApiError("request aborted", 0, undefined)),
    );
    xhr.addEventListener("load", () => {
      const body = parseJson(xhr.responseText);
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(
          new ApiError(
            extractErrorMessage(body, xhr.responseText, `HTTP ${xhr.status}`),
            xhr.status,
            body,
          ),
        );
        return;
      }
      resolve(normalizeNumericFields(body) as T);
    });
    xhr.send(form);
  });
}

export function shouldRetryApiFailure(
  failureCount: number,
  error: Error,
): boolean {
  if (
    error instanceof ApiError &&
    (error.status === 401 || error.status === 403)
  ) {
    return false;
  }
  return failureCount < 2;
}

export function normalizeNumericFields<T>(value: T): T {
  return normalizeValue(value) as T;
}

async function readApiResponse<T>(response: Response): Promise<T> {
  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const body = parseJson(text);

  if (!response.ok) {
    throw new ApiError(
      extractErrorMessage(body, text, `HTTP ${response.status}`),
      response.status,
      body,
    );
  }

  return normalizeNumericFields(body) as T;
}

function parseJson(text: string): unknown {
  if (!text.trim()) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

function extractErrorMessage(
  body: unknown,
  text: string,
  fallback: string,
): string {
  if (typeof body === "object" && body !== null) {
    const record = body as Record<string, unknown>;
    if (typeof record.error === "string" && record.error.trim()) {
      return record.error;
    }
    if (typeof record.message === "string" && record.message.trim()) {
      return record.message;
    }
    if (typeof record.error === "object" && record.error !== null) {
      const error = record.error as Record<string, unknown>;
      if (typeof error.message === "string" && error.message.trim()) {
        return error.message;
      }
    }
  }
  return text.trim() || fallback;
}

const NUMERIC_RESPONSE_FIELDS = new Set([
  "active_capture_mode",
  "active_capture_started_at",
  "active_profile_count",
  "ambient_count",
  "ambient_device_count",
  "ambient_latest_activity_at",
  "ambient_newest_started_at",
  "ambient_oldest_started_at",
  "ambient_processing_count",
  "ambient_ready_count",
  "ambient_retrying_count",
  "audio_chunk_count",
  "average_match_confidence",
  "block_count",
  "byte_size",
  "captured_at",
  "chunk_count",
  "cluster_count",
  "completed_at",
  "consented_at",
  "created_at",
  "duration_ms",
  "end_ms",
  "ended_at",
  "first_captured_at",
  "last_activity_at",
  "last_captured_at",
  "last_matched_at",
  "last_seen_at",
  "match_confidence",
  "match_confidence_floor",
  "matched_cluster_count",
  "matched_speech_ms",
  "mode",
  "occurred_at",
  "picked_up_at",
  "processing_finished_at",
  "processing_started_at",
  "profile_count",
  "profiles_with_embeddings",
  "profiles_with_sample_audio",
  "quality_score",
  "quiet_for_ms",
  "sample_byte_size",
  "score",
  "segment_count",
  "session_ended_at",
  "session_relative_start_ms",
  "session_started_at",
  "start_ms",
  "started_at",
  "total_byte_size",
  "total_duration_ms",
  "total_speech_ms",
  "unmatched_cluster_count",
  "unresolved_speech_ms",
  "updated_at",
]);

function normalizeValue(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(normalizeValue);
  if (value === null || typeof value !== "object") return value;

  const out: Record<string, unknown> = {};
  for (const [key, raw] of Object.entries(value)) {
    const normalized = normalizeValue(raw);
    if (typeof normalized === "string" && NUMERIC_RESPONSE_FIELDS.has(key)) {
      const numberValue = Number(normalized);
      out[key] = Number.isFinite(numberValue) ? numberValue : normalized;
    } else {
      out[key] = normalized;
    }
  }
  return out;
}
